import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ExternalLink, Loader2, CheckCircle2, Clock, XCircle, ClipboardList, Images, FileText, ChevronRight, ShieldCheck, Play, ListChecks } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "./ui/sheet";
import { toast } from "sonner";
import { api } from "../lib/api";
import { RunStep } from "./workflow/RunStep";
import { WorkflowRun } from "./WorkflowRun";
import { useFinalOutput } from "../hooks/useFinalOutput";
import { onUserEvent, coalesce, useUserSocketHold } from "../lib/userEvents";
import { Markdown } from "./Markdown";
import { MediaList } from "./MessageExtras";
import { StepDetail } from "./StepDetail";
import { DocVersions } from "./DocVersions";

const STATUS = { completed: ["#10B981", "Selesai"], running: ["#8B5CF6", "Berjalan"], queued: ["#F59E0B", "Antre"], waiting_approval: ["#F59E0B", "Perlu persetujuan"], paused: ["#F97316", "Dijeda"], failed: ["#EF4444", "Gagal"], scheduled: ["#2F6BFF", "Terjadwal"], cancelled: ["#94A3B8", "Batal"], draft: ["#94A3B8", "Draft"], pending: ["#94A3B8", "Belum mulai"] };
const Badge = ({ status }) => { const [c, l] = STATUS[status] || ["#94A3B8", status]; return <span className="shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold" style={{ background: `${c}22`, color: c }} data-testid="task-panel-status">{l}</span>; };

function useTask(taskId) {
  const [task, setTask] = useState(null);
  const [tick, setTick] = useState(0);
  useUserSocketHold(["queued", "running"].includes(task?.status));
  useEffect(() => {
    if (!taskId) return undefined;
    let alive = true;
    const load = () => api.get(`/tasks/${taskId}`).then((r) => alive && setTask(r.data)).catch(() => {});
    load();
    const off = onUserEvent(["task_update", "ws_state"], (e) => { if (e.type === "ws_state" ? e.connected : (!e.task_id || e.task_id === taskId)) coalesce(`task-${taskId}`, load); }); // trigger → GET
    return () => { alive = false; off(); };
  }, [taskId, tick]);
  return [task, () => setTick((x) => x + 1)];
}

// Compact clickable card under an assistant message that produced a Workspace task (document / image set).
export function TaskCard({ m, onOpen }) {
  const [task] = useTask(m.task_id);
  if (!m.task_id) return null;
  const done = (task?.steps || []).filter((s) => s.status === "completed").length;
  const total = (task?.steps || []).length;
  const isImg = (m.tool || task?.type) === "image_set" || task?.type === "image_set";
  return (
    <button onClick={() => onOpen(m.task_id)} data-testid="task-card" className="mt-2 flex w-full items-center gap-3 rounded-xl border border-[#2F6BFF]/25 bg-[#EEF3FF] p-3 text-left transition hover:border-[#2F6BFF]/60 hover:bg-[#E0E9FF]">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#2F6BFF] text-white">{isImg ? <Images size={16} /> : <FileText size={16} />}</span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-xs font-bold text-slate-900" data-testid="task-card-title">{task?.goal || "Tugas Ruang Kerja"}</span>
        <span className="block text-[11px] text-slate-500">{task ? (total ? `${done}/${total} selesai · ` : "") + (task.status === "running" ? "sedang dikerjakan…" : task.status === "completed" ? "klik untuk lihat hasil" : STATUS[task.status]?.[1] || task.status) : "memuat…"}</span>
      </span>
      {task ? <Badge status={task.status} /> : <Loader2 size={14} className="animate-spin text-slate-400" />}
    </button>
  );
}

// Right-hand slide-in with the task detail (progress, media, result) — opened from TaskCard / /workspace links in chat.
export function TaskSidePanel({ taskId, cid = null, initialStepId = null, onClose }) {
  const [task, reload] = useTask(taskId);
  const nav = useNavigate();
  const [stepId, setStepId] = useState(null);
  const [wantFinal, setWantFinal] = useState(false);
  const fin = useFinalOutput(task, wantFinal);
  useEffect(() => { const k = (e) => e.key === "Escape" && (stepId ? setStepId(null) : onClose()); window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k); }, [onClose, stepId]);
  useEffect(() => { setStepId(initialStepId || null); }, [taskId, initialStepId]);
  if (!taskId) return null;
  const stepIdx = (task?.steps || []).findIndex((s) => s.id === stepId);
  const icon = (s) => s.status === "completed" ? <CheckCircle2 size={14} className="text-[#10B981]" /> : s.status === "running" ? <Loader2 size={14} className="animate-spin text-[#8B5CF6]" /> : s.status === "failed" ? <XCircle size={14} className="text-[#EF4444]" /> : s.status === "waiting_approval" ? <ShieldCheck size={14} className="text-[#F59E0B]" /> : <Clock size={14} className="text-[#94A3B8]" />;
  return (
    <>
      <div className="fixed inset-0 z-[70] bg-slate-900/30 md:hidden" onClick={onClose} />
      <aside className="fixed right-0 top-0 z-[71] flex h-full w-full max-w-md flex-col border-l border-[#E7ECF3] bg-white shadow-2xl md:absolute md:w-[420px]" data-testid="task-side-panel" style={{ animation: "fadeUp .25s ease both" }}>
        <div className="flex items-start gap-3 border-b border-[#E7ECF3] p-4">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#2F6BFF] text-white"><ClipboardList size={17} /></span>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-bold uppercase tracking-wider text-[#2F6BFF]">Tugas Ruang Kerja{task ? ` · v${task.version}` : ""}</p>
            <p className="truncate text-sm font-bold text-slate-900" data-testid="task-panel-title">{task?.goal || "Memuat…"}</p>
            {task?.summary && <p className="mt-0.5 text-xs text-slate-500">{task.summary}</p>}
          </div>
          {task && <Badge status={task.status} />}
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="task-panel-close"><X size={18} /></button>
        </div>
        {task && stepIdx >= 0 ? (
          <div className="flex-1 overflow-y-auto p-4" data-testid="task-panel-body">
            <StepDetail task={task} step={task.steps[stepIdx]} idx={stepIdx} cid={cid} onBack={() => setStepId(null)} onChanged={reload} />
          </div>
        ) : (
        <div className="flex-1 space-y-4 overflow-y-auto p-4 [&_[data-testid=task-panel-output]]:cursor-pointer" onClick={(e) => { if (e.target.closest("a,button,video,audio,[data-step]")) return; nav(`/workspace/${taskId}`); }} title="Klik untuk membuka hasil di Ruang Kerja" data-testid="task-panel-body">
          {!task && <p className="flex items-center gap-2 text-sm text-slate-400"><Loader2 size={14} className="animate-spin" /> Memuat tugas…</p>}
          {(task?.steps || []).length > 0 && (
            <div data-testid="task-panel-steps">
              <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-400">Tahapan · klik untuk detail</p>
              <div className="space-y-1.5">{task.steps.map((s, i) => (
                <button key={s.id || i} data-step onClick={() => s.id && setStepId(s.id)} className={`flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-xs text-slate-700 transition hover:border-[#2F6BFF]/50 hover:bg-[#F5F8FF] ${s.status === "waiting_approval" ? "border-amber-300 bg-amber-50" : "border-[#E7ECF3]"}`} data-testid={`task-panel-step-${i}`}>
                  {icon(s)}<span className="min-w-0 flex-1 truncate">{s.title}</span>
                  {s.status === "waiting_approval" && <span className="shrink-0 rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold text-white" data-testid={`task-panel-step-approve-badge-${i}`}>Perlu persetujuan</span>}
                  {(s.revisions || []).length > 0 && <span className="shrink-0 text-[10px] text-slate-400">v{s.revisions.length + 1}</span>}
                  <ChevronRight size={13} className="shrink-0 text-slate-300" />
                </button>))}</div>
            </div>
          )}
          {(task?.media || []).length > 0 && <div data-testid="task-panel-media"><p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-400">Hasil ({task.media.length})</p><MediaList media={task.media} /></div>}
          {task?.status === "completed" && fin.hasFinal && !fin.ready && <button onClick={() => setWantFinal(true)} disabled={fin.loading} className="w-full rounded-xl border border-dashed border-[#E7ECF3] p-3 text-left text-xs text-slate-500 disabled:opacity-60" data-testid="task-panel-load-final"><span className="line-clamp-2 opacity-70">{fin.snippet}</span> <span className="inline-flex items-center gap-1 font-semibold text-[#2F6BFF]">{fin.loading && <Loader2 size={11} className="animate-spin" />} Buka hasil lengkap</span></button>}
          {task?.status === "completed" && fin.ready && fin.text && <div className="rounded-xl border border-[#E7ECF3] p-3 text-sm text-slate-700" data-testid="task-panel-output"><Markdown content={fin.text} /></div>}
          {task?.status === "completed" && !(task.steps || []).length && <div data-step><DocVersions task={fin.ready ? { ...task, final_output: fin.text } : task} onChanged={reload} /></div>}
          {task?.status === "failed" && <p className="rounded-xl border border-[#EF4444]/40 bg-[#EF4444]/10 p-3 text-xs text-[#EF4444]">Tugas gagal. {task.error}</p>}
        </div>
        )}
        <div className="flex items-center justify-between border-t border-[#E7ECF3] p-3 text-xs">
          <span className="text-slate-500">{task ? `${task.credits_used || 0} kredit` : ""}</span>
          <button onClick={() => nav(`/workspace/${taskId}`)} className="flex items-center gap-1 font-semibold text-[#2F6BFF]" data-testid="task-panel-open-full"><ExternalLink size={12} /> Buka halaman penuh</button>
        </div>
      </aside>
    </>
  );
}


// Docked stage list on the right of a TASK ROOM (lg+): the task's stages at a glance, one click opens the stage detail (run / approve / result).
function useStages(taskId, refreshKey) {
  const [task, reload] = useTask(taskId);
  const [running, setRunning] = useState(false);
  useEffect(() => { if (refreshKey) reload(); }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const steps = task?.steps || [];
  const runAll = async () => { // the same action as «Run» in Ruang Kerja: the saved stages start, the credit card of each Agent stage follows
    setRunning(true);
    try { await api.post(`/tasks/${task.id}/run`); toast.success("Workflow dijalankan"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menjalankan workflow"); } finally { setRunning(false); }
  };
  return { task, reload, steps, done: steps.filter((s) => s.status === "completed").length, runnable: task?.status === "draft" && steps.some((s) => s.kind !== "intake"), running, runAll };
}

// Shared body of the stage panel (desktop dock + mobile sheet): the same RunStep / WorkflowRun cards as Ruang Kerja.
function StagesBody({ taskId, st, idp = "" }) {
  const nav = useNavigate();
  const { task, reload, steps, done, runnable, running, runAll } = st;
  return (
    <>
      <div className="border-b border-[#E7ECF3] bg-white p-4">
        <p className="text-[11px] font-bold uppercase tracking-wider text-[#2F6BFF]">Tahapan tugas</p>
        <p className="mt-0.5 truncate text-sm font-bold text-slate-900" data-testid={`task-stages-title${idp}`} data-no-i18n>{task?.title || task?.goal || "Memuat…"}</p>
        <div className="mt-2 flex items-center gap-2">
          {task && <Badge status={task.status} />}
          {steps.length > 0 && <span className="text-[11px] text-slate-500" data-testid={`task-stages-progress${idp}`}>{done}/{steps.length} selesai</span>}
          {runnable && <button onClick={runAll} disabled={running} className="ml-auto flex items-center gap-1 rounded-lg bg-[#10B981] px-2.5 py-1 text-[11px] font-bold text-white hover:brightness-105 disabled:opacity-60" data-testid={`task-stages-run${idp}`}>{running ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />} Jalankan workflow</button>}
        </div>
      </div>
      <div className="flex-1 space-y-2 overflow-y-auto p-3">
        {!task && <p className="flex items-center gap-2 text-xs text-slate-400"><Loader2 size={13} className="animate-spin" /> Memuat…</p>}
        {task && steps.length === 0 && <p className="rounded-xl border border-dashed border-[#E7ECF3] bg-white p-3 text-xs text-slate-500" data-testid={`task-stages-empty${idp}`}>Belum ada tahapan. Minta asisten mengusulkan tahapannya di chat — setelah kamu setuju, tahapan dibuat di sini.</p>}
        {task && steps.length > 0 && (task.status === "draft"
          ? steps.map((s, i) => <RunStep key={s.id || i} task={task} s={s} i={i} onDone={reload} />)
          : <WorkflowRun task={task} onDone={reload} compact />)}
      </div>
      <div className="border-t border-[#E7ECF3] bg-white p-3 text-xs">
        <button onClick={() => nav(`/workspace/${taskId}`)} className="flex items-center gap-1 font-semibold text-[#2F6BFF]" data-testid={`task-stages-open-full${idp}`}><ExternalLink size={12} /> Buka di Ruang Kerja</button>
      </div>
    </>
  );
}

export function TaskStagesDock({ taskId, refreshKey }) {
  const st = useStages(taskId, refreshKey);
  return (
    <aside className="hidden w-[26rem] shrink-0 flex-col border-l border-[#E7ECF3] bg-[#F8FAFC] lg:flex" data-testid="task-stages-dock">
      <StagesBody taskId={taskId} st={st} />
    </aside>
  );
}

// Small screens: a header button with the progress badge opens the same panel as a bottom sheet.
export function TaskStagesSheet({ taskId, refreshKey }) {
  const [open, setOpen] = useState(false);
  const st = useStages(taskId, refreshKey);
  const waiting = st.steps.some((s) => s.status === "waiting_approval");
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button title="Tahapan tugas" data-testid="task-stages-sheet-btn" className={`flex h-9 shrink-0 items-center gap-1.5 rounded-lg border px-2.5 text-xs font-semibold lg:hidden ${waiting ? "border-amber-300 bg-amber-50 text-amber-700" : "border-[#E6EAF2] bg-white text-slate-700"}`}>
          <ListChecks size={15} /> <span data-testid="task-stages-sheet-progress">{st.steps.length ? `${st.done}/${st.steps.length}` : "Tahapan"}</span>
        </button>
      </SheetTrigger>
      <SheetContent side="bottom" className="flex h-[85vh] flex-col gap-0 rounded-t-2xl bg-[#F8FAFC] p-0" data-testid="task-stages-sheet">
        <SheetTitle className="sr-only">Tahapan tugas</SheetTitle>
        <SheetDescription className="sr-only">Daftar tahapan tugas ini dengan aksinya</SheetDescription>
        <StagesBody taskId={taskId} st={st} idp="-sheet" />
      </SheetContent>
    </Sheet>
  );
}
